import React, { useContext, useState, useMemo, useEffect } from 'react';
import parse from 'html-react-parser';
import { Link } from 'react-scroll';
import 'swiper/css/swiper.css';

import BabyfaceSalesMenu from './BabyfaceSalesMenu';
import BabyfaceSalesDrawer from './BabyfaceSalesDrawer';

import { Flex } from '../../../components/common/atom/Layout';
import { Text } from '../../../components/common/atom/Text';
import { BulletList } from '../../../components/common/molecules/BulletList';
import { Table } from '../../../components/common/organism/Table';

import TEXT from '../../../lib/text/textSalesBabyface.json';
import * as IMG from '../../../lib/image/ServiceImages';
import * as PATH from '../../../lib/routes/path';

import useBabyfaceSales from '../../../Hooks/sales/useBabyfaceSales';
import ThemeStyle, { SizeToRem } from '../../../lib/style/ThemeStyle';
import ModalContext from '../../../modules/contexts/Modalcontext';
import {
  DELIVERY,
  PDPTemplate,
  QUESTION,
  REFUND,
} from '../../../components/common/template/PDPTemplate';
import AffiliateContext from '../../../modules/contexts/AffiliateContext';
import { HE_COUPON_CODE } from '../../he/Results/HeFinalProductList';

const BabyfaceView = ({ data }) => {
  const [isDiscountApplied, setDiscountApplied] = useState(false);
  const { state: { isAffiliateOutlink, affiliateId, affiliateCampaign } } = useContext(AffiliateContext);

  const autumnExpireDate = new Date(9999, 12, 31, 0, 0, 0);
  const AUTUMN_COUPON_CODE = '25_09_AUTUMN_DISCOUNT';
  
  // 세션에서 쿠폰 코드 배열 가져오기 (기존 단일 문자열 호환 지원)
  const getPreSelectedVoucherCodes = () => {
    const preSelectedVoucherCodes = sessionStorage.preSelectedVoucherCodes;
    if (preSelectedVoucherCodes) {
      try {
        return JSON.parse(preSelectedVoucherCodes);
      } catch (e) {
        return [];
      }
    }
    // 기존 단일 문자열 형식 호환
    const preSelectedVoucherCode = sessionStorage.preSelectedVoucherCode;
    return preSelectedVoucherCode ? [preSelectedVoucherCode] : [];
  };

  const setPreSelectedVoucherCodes = (codes) => {
    if (Array.isArray(codes) && codes.length > 0) {
      sessionStorage.setItem('preSelectedVoucherCodes', JSON.stringify(codes));
      // 기존 단일 문자열도 첫 번째 쿠폰으로 유지 (호환성)
      sessionStorage.setItem('preSelectedVoucherCode', codes[0]);
    } else {
      sessionStorage.removeItem('preSelectedVoucherCodes');
      sessionStorage.removeItem('preSelectedVoucherCode');
    }
  };

  const preSelectedVoucherCodes = getPreSelectedVoucherCodes();
  
  console.log('AUTUMN 쿠폰 발급 체크 시작:', {
    preSelectedVoucherCodes,
    autumnExpireDate,
    currentDate: new Date(),
    isBeforeExpireDate: new Date() < autumnExpireDate
  });
  
  // HE 쿠폰 코드 체크 (24_07_HE_DISCOUNT 또는 HE_DISCOUNT_로 시작하는 모든 쿠폰 포함)
  const isHECouponSelected = preSelectedVoucherCodes.some(code => 
    code === HE_COUPON_CODE || 
    code.startsWith('24_07_HE_DISCOUNT_') ||
    code.startsWith('HE_DISCOUNT_')
  );
  
  const hasAutumnCoupon = preSelectedVoucherCodes.includes(AUTUMN_COUPON_CODE);
  
  console.log('AUTUMN 쿠폰 발급 조건 확인:', {
    isHECouponSelected,
    hasAutumnCoupon,
    isBeforeExpireDate: new Date() < autumnExpireDate,
    willIssueAutumnCoupon: new Date() < autumnExpireDate && !hasAutumnCoupon
  });
  
  // AUTUMN 쿠폰 발급 처리 (세션 저장)
  useEffect(() => {
    if (new Date() < autumnExpireDate && !hasAutumnCoupon) {
      if (isHECouponSelected) {
        console.log('AUTUMN 쿠폰 발급: HE 쿠폰과 함께 발급', {
          existingCodes: preSelectedVoucherCodes,
          newCouponCode: AUTUMN_COUPON_CODE
        });
        const updatedCodes = [...preSelectedVoucherCodes, AUTUMN_COUPON_CODE];
        console.log('AUTUMN 쿠폰 발급 완료 (HE 쿠폰과 함께):', {
          updatedCodes
        });
        setPreSelectedVoucherCodes(updatedCodes);
      } else {
        console.log('AUTUMN 쿠폰 발급: 단독 발급', {
          newCouponCode: AUTUMN_COUPON_CODE
        });
        console.log('AUTUMN 쿠폰 발급 완료 (단독):', {
          codes: [AUTUMN_COUPON_CODE]
        });
        setPreSelectedVoucherCodes([AUTUMN_COUPON_CODE]);
      }
    }
  }, [hasAutumnCoupon, isHECouponSelected, preSelectedVoucherCodes, autumnExpireDate]);

  // babyface 객체를 useMemo로 생성하여 activeCampaigns와 eventPrice를 반영
  const babyface = useMemo(() => {
    const baseBabyface = {
      title: textSalesBabyfaceTitle,
      instruction: textSalesBabyfaceInstruction,
      ...data,
    };

    // 제휴사 할인 처리
    if (isAffiliateOutlink && affiliateId === "babyface" && affiliateCampaign.startsWith("20lastdiscount")) {
      baseBabyface.activeCampaigns = [{ description: '3일 한정 수량 혜택', discountAmount: 0 }];
      baseBabyface.eventPrice = baseBabyface.price * 0.8;
      return baseBabyface;
    }

    // AUTUMN 쿠폰이 있으면 할인 가격 적용
    const currentVoucherCodes = getPreSelectedVoucherCodes();
    const hasAutumn = currentVoucherCodes.includes(AUTUMN_COUPON_CODE);
    const hasShippingCoupon = currentVoucherCodes.some(code => 
      code && code.startsWith('HE_DISCOUNT_') && code.endsWith('_SHIPPING')
    );
    
    if (new Date() < autumnExpireDate && hasAutumn) {
      baseBabyface.activeCampaigns = [{ description: '블랙 프라이데이 할인 가격', discountAmount: 7100 }];
      baseBabyface.eventPrice = 24900;
      console.log('babyface 객체 생성 - AUTUMN 할인 적용:', {
        activeCampaigns: baseBabyface.activeCampaigns,
        eventPrice: baseBabyface.eventPrice,
        currentVoucherCodes
      });
    }
    
    // HE_DISCOUNT_*_SHIPPING 쿠폰이 있으면 배송비 무료 플래그 설정
    if (hasShippingCoupon) {
      baseBabyface.freeShipping = true;
      console.log('babyface 객체 생성 - 배송비 무료 쿠폰 적용:', {
        freeShipping: true,
        shippingCouponCodes: currentVoucherCodes.filter(code => 
          code && code.startsWith('HE_DISCOUNT_') && code.endsWith('_SHIPPING')
        )
      });
    }

    return baseBabyface;
  }, [data, isAffiliateOutlink, affiliateId, affiliateCampaign, autumnExpireDate, preSelectedVoucherCodes]);

  const {
    isActiveCampaigns,
    salesOptions,
    deadline,
    doGetDeadlineBody,
  } = useBabyfaceSales({ babyface });

  if (isAffiliateOutlink && affiliateId === "babyface" && affiliateCampaign.startsWith("20lastdiscount") && salesOptions && !isDiscountApplied){
    salesOptions.forEach(option => option.options.forEach(subOption => subOption.price *= 0.80));
    setDiscountApplied(true);
  }

  if (salesOptions) {
    const additionalOptions = salesOptions.find(item => item.name === '추가옵션').options;
    const idx = additionalOptions.findIndex(option => option.name === '편백나무 디데이 달력');
    additionalOptions.splice(idx, 1);
  }

  const {
    actions: { onModal },
  } = useContext(ModalContext);

  return (
    <PDPTemplate
      productInfo={babyface}
      thumbnailImageList={SALES_ITEM_IMAGE_LIST}
      tabContent={{
        deliveryTab: TITLE_DESCRIPTION,
        qnaTab: QUESTION_CONTENT,
      }}
      bannerList={BABYFACE_BANNER_LIST}
      deliveryInfo={
        deadline && (
          <Flex
            column
            padding={`1.25rem 1.25rem 2.5rem`}
            alignItems="flex-start"
          >
            <Flex
              justifyContent="space-between"
              style={{ width: '100%' }}
              padding={SizeToRem(5)}
            >
              <Text size={12} color="orangePink">
                {textEstimatedDeliveryDate}
              </Text>
              <Text size={10}>
                ({deadline.currentTime} {textStandard})
              </Text>
            </Flex>
            <Table header={DEADLINE_HEADER} body={doGetDeadlineBody()} />
            <BulletList
              contents={textOrderGuide.map((item, idx) => {
                return (
                  <>
                    {parse(item)}{' '}
                    {idx === 0 && (
                      <Link
                        to="example"
                        spy={true}
                        smooth={true}
                        duration={500}
                        offset={-50}
                      >
                        <Text
                          style={{ textDecoration: 'underline' }}
                          onClick={() =>
                            onModal({
                              title: '입체초음파 사진 예시',
                              content: <ExampleImage />,
                            })
                          }
                        >
                          {titleExampleImage}
                        </Text>
                      </Link>
                    )}
                  </>
                );
              })}
              ulStyle={{
                width: '100%',
                margin: 0,
                padding: `${SizeToRem(11)} ${SizeToRem(30)}`,
                borderTop: `solid 1px ${ThemeStyle.color.pinkishGrey}`,
                borderBottom: `solid 1px ${ThemeStyle.color.pinkishGrey}`,
                span: { fontSize: SizeToRem(12) },
              }}
              liStyle={{ fontSize: SizeToRem(12) }}
            />
          </Flex>
        )
      }
    >
      <BabyfaceSalesMenu />
      <BabyfaceSalesDrawer
        babyface={babyface}
        isActiveCampaigns={isActiveCampaigns}
        salesOptions={salesOptions}
      />
    </PDPTemplate>
  );
};

export default BabyfaceView;

const {
  textOption,
  textConversionDeadline,
  textDeleveryDeadline,
  textEstimatedDeliveryDate,
  textStandard,
  textOrderGuide,
  titleUltraAvaliable,
  titleUltraUnavaliable,
  titleExampleImage,
  titleDelivery,
  notiDelivery,
  titleRefund,
  notiRefund,
  titleQuestion,
  notiQuestion,
  titleAnalyze,
  notiAnalyze,
  altClearfaceLink,
  textSalesBabyfaceTitle,
  textSalesBabyfaceInstruction,
} = TEXT;

const DEADLINE_HEADER = [
  textOption,
  textConversionDeadline,
  textDeleveryDeadline,
];
const {
  IMG_ULTRA_AVALIABLE,
  IMG_ULTRA_UNAVALIABLE,
  IMG_CLEAFACE,
  SALES_ITEM_IMAGE_LIST,
} = IMG;

const EXAMPLEI_MAGES = [
  [
    titleUltraAvaliable,
    '코/입 부분의 형태를 일부분 이상 확인 가능한 상태',
    IMG_ULTRA_AVALIABLE,
  ],
  [
    titleUltraUnavaliable,
    '코/입 부분의 형태를 알 수 없는 상태',
    IMG_ULTRA_UNAVALIABLE,
  ],
].map((data) => ({ title: data[0], description: data[1], imgSrc: data[2] }));

export const ExampleImage = () => {
  return EXAMPLEI_MAGES.map((data, idx) => (
    <Flex alignItems="flex-start" column key={idx} padding="0 0 0.75rem ">
      <Text weight="medium">{data.title}</Text>
      <Text size={12} style={{ marginBottom: '0.75rem' }} lineHeight="1.5">
        {data.description}
      </Text>
      <img src={data.imgSrc} alt={data.title} width="100%" />
    </Flex>
  ));
};

const QUESTION_CONTENT = Array(5)
  .fill({ question: '', answer: '' })
  .map((_, idx) => {
    return {
      question: TEXT[`titleQuestion${idx + 1}`],
      answer: TEXT[`notiAnswer${idx + 1}`],
    };
  });

const TITLE_DESCRIPTION = [
  [DELIVERY, titleDelivery, notiDelivery],
  [REFUND, titleRefund, notiRefund],
  [QUESTION, titleQuestion, notiQuestion],
  ['analyze', titleAnalyze, notiAnalyze],
].map((data) => ({
  id: data[0],
  title: data[1],
  description: data[2],
}));

const BABYFACE_BANNER_LIST = [
  {
    src: IMG_CLEAFACE,
    alt: altClearfaceLink,
    path: PATH.SALES_CLEARFACE,
  },
];
